import { getAuthHeaders } from './authToken';

const API_BASE = process.env.REACT_APP_API_URL;

// Delegates to the shared helper so every call carries the signed-in user's Zitadel access token.
const getHeaders = (includeContentType = true) => getAuthHeaders(includeContentType);

// Statuses an operator can transition a request to from the portal.
export const DRIVER_COACHING_STATUSES = ['NEW', 'IN_PROGRESS', 'DONE'];

// System-assigned status for requests routed to an external delivery partner (DB-only, no Slack).
export const DRIVER_COACHING_STATUS_PARTNER_DRIVER = 'PartnerDriver';

// All statuses that can appear in the list (used for the status filter).
export const DRIVER_COACHING_FILTER_STATUSES = [
  ...DRIVER_COACHING_STATUSES,
  DRIVER_COACHING_STATUS_PARTNER_DRIVER,
];

// Where a request originated. Defaults to OpsPortal on the API side.
export const DRIVER_COACHING_SOURCES = ['OpsPortal', 'ZenDesk'];

// Delivery partners that requests can be routed to (status = PartnerDriver).
export const DRIVER_COACHING_PARTNERS = ['Promed', 'Hovership', 'Optima', 'OSM'];

/**
 * Submit a driver coaching request.
 * @param {{
 *   parcelId: number,
 *   note?: string,
 *   submittedByFullName?: string,
 *   submittedByEmailAddress?: string,
 *   coachingReason?: string,
 *   coachingDriverFullName?: string,
 *   source?: 'OpsPortal' | 'ZenDesk'
 * }} payload
 */
export const submitDriverCoachingRequest = async (payload) => {
  const response = await fetch(`${API_BASE}/api/driver-coaching/submit`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw await response.json().catch(() => new Error('Failed to submit coaching request'));
  }

  return response.json();
};

/**
 * List driver coaching requests with pagination/filters.
 * @returns {Promise<{totalCount: number, page: number, pageSize: number, items: DriverCoachingRequest[]}>}
 *
 * DriverCoachingRequest shape:
 * {
 *   id, parcelId, btParcelId, btShipmentId,
 *   note, coachingReason, coachingDriverFullName,
 *   submittedByFullName, submittedByEmailAddress,
 *   status, source, partnerName, metroCode,
 *   slackChannelId, createdAt, updatedAt
 * }
 */
export const getDriverCoachingRequests = async ({
  page = 1,
  pageSize = 25,
  status = '',
  parcelId = '',
  btParcelId = '',
  submittedByEmail = '',
  source = '',
  metroCode = '',
  partner = '',
  fromDate = '',
  toDate = '',
} = {}) => {
  const queryParams = new URLSearchParams({
    page: page.toString(),
    pageSize: pageSize.toString(),
  });

  if (status) queryParams.append('status', status);
  if (parcelId) queryParams.append('parcelId', String(parcelId));
  if (btParcelId) queryParams.append('btParcelId', btParcelId);
  if (submittedByEmail) queryParams.append('submittedByEmail', submittedByEmail);
  if (source) queryParams.append('source', source);
  if (metroCode) queryParams.append('metroCode', metroCode);
  if (partner) queryParams.append('partner', partner);
  if (fromDate) queryParams.append('fromDate', fromDate);
  if (toDate) queryParams.append('toDate', toDate);

  const response = await fetch(`${API_BASE}/api/driver-coaching?${queryParams.toString()}`, {
    method: 'GET',
    headers: getHeaders(false),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch driver coaching requests');
  }

  return response.json();
};

export const getDriverCoachingRequestById = async (id) => {
  const response = await fetch(`${API_BASE}/api/driver-coaching/${id}`, {
    method: 'GET',
    headers: getHeaders(false),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch driver coaching request');
  }

  return response.json();
};

/**
 * @param {number} id
 * @param {'NEW' | 'IN_PROGRESS' | 'DONE'} status
 */
export const updateDriverCoachingStatus = async (id, status) => {
  const response = await fetch(`${API_BASE}/api/driver-coaching/${id}/status`, {
    method: 'PATCH',
    headers: getHeaders(),
    body: JSON.stringify({ status }),
  });

  if (!response.ok) {
    throw await response.json().catch(() => new Error('Failed to update status'));
  }

  return response.json();
};
